import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useConfig } from "../contexts/ConfigContext";
import {
  getCachedInvitationConfigs,
  getInvitPubLink,
  peekInvitationConfigs,
} from "../services/invitation.service";
import {
  InvitationChannelEnum,
  InvitationConfigType,
} from "../types/invitation.types";

/**
 * The two participant-facing invitation affordances — « Partager » (public link)
 * and « Inviter <rôle> » (email) — and the rules that decide whether to offer them.
 *
 * Extracted from the burger Menu so the Compte page can carry the same two rows.
 * Mental+ now opens Compte straight from the header and never renders the drawer,
 * so these had to live somewhere both surfaces can reach; a second copy of the
 * "which config, and is that role already taken?" rules would have drifted.
 *
 * The rule, in both places: a channel is offered only when the researcher authored
 * an invitation config FROM this participant's role AND the caller's dossier does
 * not already hold a registered member of the target role (`to_role_already_joined`)
 * — the second parent who registered months ago should not still be "invitable".
 * Nothing about parents/roles is hard-coded; the role's own label is the button text.
 */
export interface InvitationAffordances {
  /** A public link is available to share. */
  canShare: boolean;
  /** Label of the role an email invitation can be sent to; null = offer nothing. */
  inviteRoleLabel: string | null;
  /** Mint a public link and hand it to the share sheet (clipboard fallback). */
  share: () => Promise<void>;
}

export function useInvitationAffordances(
  isConnected: boolean
): InvitationAffordances {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const { study } = useConfig();

  /**
   * Seeded from the cache Tabs.tsx warms at app entry, so a page that renders these
   * rows paints with its final shape instead of inserting them a request later.
   * Null = genuinely not known yet (cold cache): the rows stay hidden rather than
   * appearing and then retracting, which is the less jarring of the two guesses.
   */
  const [configs, setConfigs] = useState<InvitationConfigType[] | null>(() =>
    peekInvitationConfigs(study?.id, language)
  );

  useEffect(() => {
    if (!isConnected || !study?.id) return;
    let cancelled = false;
    getCachedInvitationConfigs(study.id, language)
      .then((fetched) => {
        if (!cancelled) setConfigs(fetched);
      })
      .catch(() => {
        /* fail-soft: the entries simply stay hidden */
      });
    return () => {
      cancelled = true;
    };
  }, [isConnected, study?.id, language]);

  // Nothing to offer once that role has already joined the dossier — the second
  // parent who registered months ago should not still be "invitable".
  const emailConfig = configs?.find(
    (config) =>
      config.channel === InvitationChannelEnum.EMAIL &&
      !config.to_role_already_joined
  );
  const inviteRoleLabel = emailConfig
    ? emailConfig.to_role_label ?? emailConfig.to_role
    : null;
  const canShare = !!configs?.some(
    (config) =>
      config.channel === InvitationChannelEnum.PUBLICLINK &&
      !config.to_role_already_joined
  );

  const share = useCallback(async () => {
    if (!study) return;

    try {
      const invitationConfigs = await getCachedInvitationConfigs(
        study.id,
        language
      );

      // A shareable link lets someone join exactly like an invitation does, so it
      // obeys the same per-dossier limit: a role the dossier is already full of is
      // not offered.
      const publicLinkConfig = invitationConfigs.find(
        (config) =>
          config.channel === InvitationChannelEnum.PUBLICLINK &&
          !config.to_role_already_joined
      );

      if (!publicLinkConfig) {
        alert(t("menu_share_no_public_link"));
        return;
      }

      const { data: link } = await getInvitPubLink(
        { studyId: study.id },
        { invitation_config_id: publicLinkConfig.id }
      );

      const shareUrl = link as string;

      if (navigator.share) {
        try {
          await navigator.share({
            title: t("menu_share_title"),
            text: t("menu_share_text"),
            url: shareUrl,
          });
        } catch (err) {
          console.error("Share aborted or failed:", err);
        }
      } else {
        try {
          await navigator.clipboard?.writeText(shareUrl);
          alert(t("menu_share_link_copied"));
        } catch (err) {
          alert(t("menu_share_not_supported"));
          console.error("Clipboard share failed:", err);
        }
      }
    } catch (e) {
      console.error("Error while generating invitation token:", e);
      alert(t("menu_share_link_error"));
    }
  }, [study, language, t]);

  // Signed out there is nothing to share and no way to check: minting a public
  // link is an authenticated call, and the effect that would answer the question
  // never ran. Offering the row anyway (as the drawer used to on the welcome
  // screen) could only end in "lien indisponible".
  return { canShare: isConnected && canShare, inviteRoleLabel, share };
}
